import { useState, useMemo } from 'react';
import { Box, Flex, Heading, Tag, Button, Input, Select, Menu, MenuButton, MenuList, MenuItem, Table, Thead, Tbody, Tr, Th, Td, IconButton, Icon, InputGroup, InputLeftElement, useToast, Text } from '@chakra-ui/react';
import { SearchIcon, DragHandleIcon, CopyIcon, RepeatClockIcon, ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons';
import { ProcInfo } from '../../types/stats';
import { parsePercentage, parseMemoryMB } from '../../utils/stats';

interface ProcessesTableProps {
  data: ProcInfo[];
  paused: boolean;
}

type SortField = 'pid' | 'name' | 'cpu' | 'mem';
type SortDirection = 'asc' | 'desc';

export function ProcessesTable({ data, paused }: ProcessesTableProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState<SortField>('cpu');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [pageSize, setPageSize] = useState<number>(10);
  const [currentPage, setCurrentPage] = useState(1);
  const toast = useToast();

  // Filter and sort data
  const filteredAndSortedData = useMemo(() => {
    let filtered = data.filter(proc => 
      proc.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      proc.pid.toString().includes(searchTerm)
    );

    filtered.sort((a, b) => {
      let aVal: number | string;
      let bVal: number | string;

      switch (sortField) {
        case 'pid':
          aVal = a.pid;
          bVal = b.pid;
          break;
        case 'name':
          aVal = a.name.toLowerCase();
          bVal = b.name.toLowerCase();
          break;
        case 'cpu':
          aVal = parsePercentage(a.cpu);
          bVal = parsePercentage(b.cpu);
          break;
        case 'mem':
          aVal = parsePercentage(a.mem);
          bVal = parsePercentage(b.mem);
          break;
        default:
          aVal = 0;
          bVal = 0;
      }

      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return sortDirection === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      }

      return sortDirection === 'asc' ? (aVal as number) - (bVal as number) : (bVal as number) - (aVal as number);
    });

    return filtered;
  }, [data, searchTerm, sortField, sortDirection]);

  // Pagination
  const totalPages = Math.ceil(filteredAndSortedData.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const paginatedData = filteredAndSortedData.slice(startIndex, startIndex + pageSize);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
    setCurrentPage(1);
  };

  const copyPid = (pid: number) => {
    navigator.clipboard.writeText(pid.toString());
    toast({
      title: "PID Copied",
      description: `Process ID ${pid} copied to clipboard`,
      status: "success"
    });
  };

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? '↑' : '↓';
  };

  return (
    <Box borderWidth="1px" borderRadius="xl" p={4} position="relative" overflow="hidden">
      {paused && (
        <Tag position="absolute" top={2} right={2} colorScheme="yellow">Paused</Tag>
      )}
      
      <Flex as="header" alignItems="center" justifyContent="space-between" pb={4}>
        <Heading as="h3" size="sm" display="flex" alignItems="center" gap={2}>
          <RepeatClockIcon />
          Top Processes ({filteredAndSortedData.length})
        </Heading>
      </Flex>
      
      <Flex direction={{ base: 'column', sm: 'row' }} gap={2} justifyContent="space-between" mb={4}>
        <InputGroup maxW="sm">
          <InputLeftElement pointerEvents="none">
            <SearchIcon color="gray.300" />
          </InputLeftElement>
          <Input
            placeholder="Search by name or PID..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
          />
        </InputGroup>
        
        <Select value={pageSize.toString()} onChange={(e) => {
          setPageSize(parseInt(e.target.value));
          setCurrentPage(1);
        }} w="100px" borderRadius="2xl" >
          <option value="5">5</option>
          <option value="10">10</option>
          <option value="20">20</option>
        </Select>
      </Flex>

      <Box borderWidth="1px" borderRadius="2xl" overflowX="auto">
        <Table variant="simple">
          <Thead>
            <Tr>
              <Th cursor="pointer" onClick={() => handleSort('name')}>Name {getSortIcon('name')}</Th>
              <Th cursor="pointer" onClick={() => handleSort('pid')}>PID {getSortIcon('pid')}</Th>
              <Th cursor="pointer" onClick={() => handleSort('cpu')} isNumeric>CPU% {getSortIcon('cpu')}</Th>
              <Th cursor="pointer" onClick={() => handleSort('mem')} isNumeric>Memory {getSortIcon('mem')}</Th>
              <Th></Th>
            </Tr>
          </Thead>
          <Tbody>
            {paginatedData.length === 0 ? (
              <Tr>
                <Td colSpan={5} textAlign="center" py={4} color="gray.500">
                  {searchTerm ? 'No processes found matching search' : 'No processes found'}
                </Td>
              </Tr>
            ) : (
              paginatedData.map((proc) => {
                const cpuUsage = parsePercentage(proc.cpu);
                
                return (
                  <Tr key={proc.pid}>                      
                    <Td fontFamily="mono" fontSize="sm">{proc.name}</Td>
                    <Td fontFamily="mono" fontSize="sm">{proc.pid}</Td>
                    <Td isNumeric>
                      <Tag 
                        colorScheme={cpuUsage > 50 ? 'red' : cpuUsage > 20 ? 'yellow' : 'gray'}
                        fontSize="xs" 
                        fontFamily="mono"
                      >
                        {proc.cpu}
                      </Tag>
                    </Td>
                    <Td isNumeric fontFamily="mono" fontSize="sm">{proc.mem}</Td>
                    <Td>
                      <Menu>
                        <MenuButton as={IconButton} variant="ghost" size="sm" icon={<DragHandleIcon />} />
                        <MenuList>
                          <MenuItem onClick={() => copyPid(proc.pid)} icon={<CopyIcon />}>
                            Copy PID
                          </MenuItem>
                          <MenuItem isDisabled icon={<RepeatClockIcon />}>
                            Kill (coming soon)
                          </MenuItem>
                        </MenuList>
                      </Menu>
                    </Td>
                  </Tr>
                );
              })
            )}
          </Tbody>
        </Table>
      </Box>

      {totalPages > 1 && (
        <Flex alignItems="center" justifyContent="space-between" mt={4}>
          <Text fontSize="sm" color="gray.500">
            Showing {startIndex + 1}-{Math.min(startIndex + pageSize, filteredAndSortedData.length)} of {filteredAndSortedData.length}
          </Text>
          <Flex alignItems="center" gap={2}>
            <IconButton
              aria-label="Previous Page"
              icon={<ChevronLeftIcon />}
              size="sm"
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              isDisabled={currentPage === 1}
            />
            <Text fontSize="sm" fontFamily="mono">
              {currentPage} / {totalPages}
            </Text>
            <IconButton
              aria-label="Next Page"
              icon={<ChevronRightIcon />}
              size="sm"
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              isDisabled={currentPage === totalPages}
            />
          </Flex>
        </Flex>
      )}
    </Box>
  );
}
